import { Link } from "react-router-dom";
import { useData } from "data/data-provider";
import moment from "moment";

/**
 * Brand mark.
 *
 * Renders the FMS-uploaded company logo when available. When `company_logo`
 * is empty, falls back to a color-matching badge: a gold tile with the
 * company initials plus a small wordmark. The fallback uses the active
 * theme's primary / text colors so it adapts to whatever palette the
 * tenant has configured (Design A or Design B).
 */
const Logo = () => {
  const { companyDetails, setSelectedWeeks, ourCompany } = useData();
  const time = moment();
  const timeInMilliseconds = time.valueOf();

  const companyLogo = companyDetails?.company_logo
    ? `${companyDetails?.company_logo}?date=${timeInMilliseconds}`
    : "";

  const companyId = ourCompany?.company?.id;
  const logoWidth = companyId === 1592 ? 200 : 120;
  const logoHeight = companyId === 1592 ? 150 : 60;

  const handleClick = () => {
    setSelectedWeeks([]);
  };

  // Resolve a display name from any of the places it might live.
  const companyName: string =
    companyDetails?.company_name?.trim() ||
    companyDetails?.company?.name?.trim() ||
    ourCompany?.company?.name?.trim() ||
    "";

  const initials = companyName
    ? companyName
        .split(/\s+/)
        .map((w) => w[0])
        .filter(Boolean)
        .join("")
        .slice(0, 2)
        .toUpperCase()
    : "";

  return (
    // No explicit text color on the wrapper — inherit from the parent so
    // the wordmark stays legible in any context (light header bg, dark
    // footer bg, etc.). When this was hardcoded to `text-text-color`, the
    // footer rendered navy-on-navy and the company name disappeared.
    <Link
      to="/"
      onClick={handleClick}
      className="ttnc-logo group inline-flex items-center gap-3 focus:outline-none focus:ring-0"
      aria-label={companyName || "Home"}
    >
      {companyLogo ? (
        <img
          width={logoWidth}
          height={logoHeight}
          src={companyLogo}
          alt={companyName ? `${companyName} logo` : "Logo"}
        />
      ) : (
        <>
          {/* Initials badge — uses the theme's primary tones so the logo
              always matches the active color palette. */}
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-primary-200 to-primary-300 shadow-md shadow-primary-200/30 ring-1 ring-primary-200/40 transition-transform duration-300 group-hover:scale-105">
            {initials ? (
              <span className="text-base font-extrabold tracking-tight text-button-color">
                {initials}
              </span>
            ) : (
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-button-color"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="10" />
                <circle cx="12" cy="12" r="2" />
                <path d="M12 2v4" />
                <path d="m4.93 4.93 2.83 2.83" />
                <path d="M2 12h4" />
              </svg>
            )}
          </span>
          {companyName && (
            <span className="hidden sm:flex flex-col leading-tight">
              {/* Inherit color from the wrapper Link so the wordmark adapts
                  to dark or light surroundings. Subtitle uses opacity for
                  the muted look instead of a fixed secondary color token. */}
              <span className="text-base font-extrabold tracking-tight">
                {companyName}
              </span>
              <span className="text-[10px] font-medium uppercase tracking-[0.18em] opacity-70">
                Premium Rentals
              </span>
            </span>
          )}
        </>
      )}
    </Link>
  );
};

export default Logo;
